import { useState, useCallback, useEffect, useMemo } from 'react';
import { clsx } from 'clsx';
import { FieldRenderer } from './FieldRenderer';
import { isFieldLocked } from './fieldLock';
import { isEmptyValue } from './fieldValidation';
import { useMasterDocumentTranslation } from './i18n';
import { CanonTabsBar } from './CanonTabsBar';
import { buildDependentLookupProps, buildDependentsByParent, isFilterValueEmpty } from './lookupFilters';
import type {
  HeaderRendererProps,
  SectionConfig,
  FieldDescriptor,
  FieldErrorMap,
  DocumentHeaderData,
  LookupFieldConfig,
} from './MasterDocument.types';

// ─── Chevron icon ──────────────────────────────────────────

function ChevronIcon({ open, className }: { open: boolean; className?: string }) {
  return (
    <svg
      className={clsx(
        'w-4 h-4 transition-transform duration-150',
        open ? 'rotate-90' : 'rotate-0',
        className,
      )}
      viewBox="0 0 20 20"
      fill="currentColor"
    >
      <path fillRule="evenodd" d="M7.21 14.77a.75.75 0 01.02-1.06L11.168 10 7.23 6.29a.75.75 0 111.04-1.08l4.5 4.25a.75.75 0 010 1.08l-4.5 4.25a.75.75 0 01-1.06-.02z" clipRule="evenodd" />
    </svg>
  );
}

// ─── Fields Grid ───────────────────────────────────────────

function FieldsGrid({
  fields,
  values,
  errors,
  onFieldChange,
  docState,
  modeForm,
  hasLines,
  gridColumns,
  lookupConfigs,
  fetcher,
  onRecordSelect,
  paddingX = 'px-4',
}: {
  fields: FieldDescriptor[];
  values: DocumentHeaderData;
  errors?: FieldErrorMap;
  onFieldChange: (key: string, value: unknown) => void;
  docState: string;
  modeForm: 'create' | 'edit' | 'view';
  hasLines: boolean;
  gridColumns: 1 | 2 | 3 | 4;
  lookupConfigs?: Record<string, LookupFieldConfig>;
  fetcher?: unknown;
  onRecordSelect?: (fieldKey: string, record: Record<string, unknown> | null) => void;
  /**
   * Padding horizontal de la rejilla. Lo decide el LLAMADOR porque los dos que hay
   * viven en cajas distintas: en el acordeón la rejilla cuelga de una cabecera con
   * `px-4` y tiene que cuadrar con ella, mientras que en el panel de pestañas el
   * gutter ya lo pone la tarjeta y un `px-4` propio se le SUMABA —el primer campo
   * quedaba 16px más adentro que la primera pestaña de su barra.
   */
  paddingX?: string;
}) {
  const sorted = [...fields].sort((a, b) => a.order - b.order);

  const gridClass = {
    1: 'grid-cols-1',
    2: 'grid-cols-1 sm:grid-cols-2',
    3: 'grid-cols-1 sm:grid-cols-2 lg:grid-cols-3',
    4: 'grid-cols-1 sm:grid-cols-2 lg:grid-cols-4',
  }[gridColumns];

  return (
    <div className={clsx('grid gap-4 py-4', paddingX, gridClass)}>
      {sorted.map((field) => {
        const isEditable = field.editableOn.includes(docState) && modeForm !== 'view';
        const locked = isFieldLocked(field, docState, hasLines);
        const spanClass = {
          1: '',
          2: 'sm:col-span-2',
          3: 'sm:col-span-2 lg:col-span-3',
          4: 'sm:col-span-2 lg:col-span-4',
        }[field.span];

        const isSearcher = field.control === 'searcher' || field.control === 'autocomplete';
        const lookupCfg = isSearcher ? lookupConfigs?.[field.key] : undefined;
        // Cascading lookups: resuelve cada padre contra los valores de cabecera.
        const depProps = buildDependentLookupProps(lookupCfg, (k) => values[k]);

        return (
          <div key={field.key} className={clsx(spanClass)}>
            <FieldRenderer
              fieldDef={field}
              value={values[field.key]}
              onChange={(v) => onFieldChange(field.key, v)}
              editable={isEditable}
              error={errors?.[field.key]}
              readOnly={modeForm === 'view'}
              lookupConfig={lookupCfg}
              fetcher={isSearcher ? fetcher : undefined}
              onRecordSelect={isSearcher ? (rec) => onRecordSelect?.(field.key, rec) : undefined}
              getLookupFilters={depProps?.getFilters}
              lookupDependencies={depProps?.dependencies}
              disabled={depProps?.parentMissing}
              locked={locked}
              lockTooltip={field.lockTooltip}
              lockTooltipKey={field.lockTooltipKey}
            />
          </div>
        );
      })}
    </div>
  );
}

// ─── Accordion Mode ────────────────────────────────────────

function AccordionSection({
  section,
  fields,
  values,
  errors,
  onFieldChange,
  docState,
  modeForm,
  hasLines,
  gridColumns,
  lookupConfigs,
  fetcher,
  onRecordSelect,
}: {
  section: SectionConfig;
  fields: FieldDescriptor[];
  values: DocumentHeaderData;
  errors?: FieldErrorMap;
  onFieldChange: (key: string, value: unknown) => void;
  docState: string;
  modeForm: 'create' | 'edit' | 'view';
  hasLines: boolean;
  gridColumns: 1 | 2 | 3 | 4;
  lookupConfigs?: Record<string, LookupFieldConfig>;
  fetcher?: unknown;
  onRecordSelect?: (fieldKey: string, record: Record<string, unknown> | null) => void;
}) {
  const [open, setOpen] = useState(section.defaultOpen);

  return (
    <div className="overflow-hidden">
      <button
        type="button"
        onClick={() => setOpen((o) => !o)}
        className={clsx(
          'flex items-center justify-between w-full px-4 py-3',
          'hover:bg-background-secondary transition-all duration-150',
          'dark:hover:bg-dark-bg-secondary',
          'focus:outline-hidden focus:ring-2 focus:ring-inset focus:ring-primary-400',
          'dark:focus:ring-dark-border-custom',
          open && section.accent && 'border-b-2',
        )}
        style={open && section.accent ? { borderBottomColor: section.accent } : undefined}
        aria-expanded={open}
      >
        <div className="flex items-center gap-2">
          <ChevronIcon open={open} className="text-content-secondary dark:text-dark-content-secondary" />
          <span className="text-sm font-bold text-content-primary dark:text-dark-content-primary">
            {section.label}
          </span>
          {section.subtitle && (
            <span className="text-xs text-content-secondary dark:text-dark-content-secondary">
              {section.subtitle}
            </span>
          )}
        </div>
      </button>

      {open && (
        <FieldsGrid
          fields={fields}
          values={values}
          errors={errors}
          onFieldChange={onFieldChange}
          docState={docState}
          modeForm={modeForm}
          hasLines={hasLines}
          gridColumns={gridColumns}
          lookupConfigs={lookupConfigs}
          fetcher={fetcher}
          onRecordSelect={onRecordSelect}
        />
      )}
    </div>
  );
}

// ─── Tabs Mode ─────────────────────────────────────────────

function TabsRenderer({
  sections,
  fieldsBySection,
  values,
  errors,
  onFieldChange,
  docState,
  modeForm,
  hasLines,
  gridColumns,
  lookupConfigs,
  fetcher,
  onRecordSelect,
}: {
  sections: SectionConfig[];
  fieldsBySection: Record<string, FieldDescriptor[]>;
  values: DocumentHeaderData;
  errors?: FieldErrorMap;
  onFieldChange: (key: string, value: unknown) => void;
  docState: string;
  modeForm: 'create' | 'edit' | 'view';
  hasLines: boolean;
  gridColumns: 1 | 2 | 3 | 4;
  lookupConfigs?: Record<string, LookupFieldConfig>;
  fetcher?: unknown;
  onRecordSelect?: (fieldKey: string, record: Record<string, unknown> | null) => void;
}) {
  const { t } = useMasterDocumentTranslation();
  const [activeTab, setActiveTab] = useState(sections[0]?.key ?? '');

  useEffect(() => {
    if (sections.length > 0 && !sections.find((s) => s.key === activeTab)) {
      setActiveTab(sections[0].key);
    }
  }, [sections, activeTab]);

  // `activeSection` se usaba sólo para pintar el subrayado con el `accent` de la sección.
  // El indicador del canon es de un color fijo (#0e79fd), así que ya no hace falta aquí.
  const activeFields = fieldsBySection[activeTab] ?? [];

  /**
   * Señalización de obligatorios por pestaña (ADR-027 §C.3).
   *
   * - **asterisco** (`hasRequired`): la pestaña contiene al menos un campo
   *   obligatorio. Se ve desde el primer render, sin haber intentado guardar.
   * - **badge** (`missingCount`): cuántos de esos obligatorios siguen bloqueando
   *   el guardado. Sólo aparece DESPUÉS de un intento, que es cuando el
   *   contenedor llena `errors`.
   *
   * Las dos marcas salen de la MISMA obligatoriedad que declara la plantilla, no
   * de dos fuentes distintas: por eso el badge cuenta únicamente campos
   * `required`, y así nunca puede haber una pestaña con número y sin asterisco
   * —la contradicción que el §C.3b describe y que el usuario no puede resolver
   * porque nada en esa pestaña parecía obligatorio—.
   *
   * `fieldsBySection` ya viene filtrado por visibilidad, así que un campo oculto
   * por condición o por permiso no infla la cuenta.
   */
  const tabsMeta = useMemo(() => {
    const errorMap = errors ?? {};
    const seIntentoGuardar = Object.keys(errorMap).length > 0;
    /*
     * La noción de "vacío" es la MISMA que usa el borde rojo del campo y la regla
     * `required` del motor de validación (`isEmptyValue`), no una copia local. Con una
     * copia, un campo con solo espacios recibía borde rojo y mensaje de obligatorio y a
     * la vez NO lo contaba este badge: dos señales de la misma obligatoriedad
     * contradiciéndose, que es exactamente lo que el §C.3b prohíbe.
     */

    /*
     * En SOLO LECTURA no se señala nada. El asterisco existe para decir «esto hay que
     * llenarlo», y en un documento que no se puede editar no hay nada que llenar:
     * marcarlo deja la pestaña afirmando un requisito que su propio campo no muestra
     * —`FieldRenderer` sólo pinta el asterisco cuando el campo es editable—, que es
     * exactamente la contradicción que el §C.3b prohíbe. Medido en dev el 2026-09-09
     * sobre una devolución aprobada: la pestaña «Generales» traía `*` y su único campo,
     * de sólo lectura, no.
     */
    const enSoloLectura = modeForm === 'view';

    return sections.map((section) => {
      const obligatorios = (fieldsBySection[section.key] ?? []).filter(
        // `required` no basta: un campo de control `readonly`/`display` NUNCA pinta
        // asterisco (ver `isReadOnly` en FieldRenderer) y el usuario tampoco puede
        // rellenarlo, así que contarlo dejaba la pestaña marcada con un pendiente que
        // nadie podía cerrar. El modo `view` es el mismo caso a nivel de documento.
        (f) => f.required && f.control !== 'readonly' && f.control !== 'display',
      );
      return {
        key: section.key,
        label: section.label,
        hasRequired: !enSoloLectura && obligatorios.length > 0,
        missingCount: !enSoloLectura && seIntentoGuardar
          ? obligatorios.filter((f) => errorMap[f.key] !== undefined || isEmptyValue(values[f.key])).length
          : 0,
      };
    });
  }, [sections, fieldsBySection, errors, values, modeForm]);

  return (
    <div>
      {/*
        Barra de pestañas del canon (ADR-027 §C.2), a través de `CanonTabsBar`.
        Antes esta barra pintaba su propio diseño —14px/600 con `border-b-2` estático y el
        fondo de la inactiva en `background-secondary`—, que no es el de MasterCrud: 12px,
        `rounded-t-lg` sobre #f8f9fc, activa en #0c6ade e indicador deslizante de 2.5px.
        Medido el 2026-09-04 sobre /app/items y /app/core/purchases/purchase-requests-direct.

        El `accent` por sección deja de pintar el subrayado: el indicador del canon es de un
        solo color (#0e79fd, Regla C.1/C.2) y admitir un color por pestaña reintroduciría la
        divergencia que este cambio cierra. El `accent` sigue vivo en el modo acordeón.
      */}
      {/*
        Con UNA sola sección no se pinta barra: una pestaña solitaria no ofrece ninguna
        elección y sólo añade una franja que el usuario no puede usar. Cuatro documentos del
        catálogo de core están en ese caso (solicitudes de compra directa, requisiciones de
        consumo, traslados directos y conteos físicos), así que no es un caso de borde.
      */}
      {sections.length > 1 && (
        <CanonTabsBar
          tabs={tabsMeta}
          activeKey={activeTab}
          onChange={setActiveTab}
          ariaLabel={t('master_document.header.sectionsAria')}
          idPrefix="header-tab"
        />
      )}

      {/*
        Panel con su relación ARIA completa. La barra emite `aria-controls`, así que sin
        este `role="tabpanel"` con el `id` que espera, el atributo apuntaba a un elemento
        que no existe: un lector de pantalla anuncia la pestaña y no encuentra la región que
        gobierna. De paso cierra, para el encabezado, el hueco que el ADR-027 §C.5a deja
        declarado como conocido en el canon de MasterCrud.
      */}
      <div
        role="tabpanel"
        id={`header-tabpanel-${activeTab}`}
        aria-labelledby={sections.length > 1 ? `header-tab-${activeTab}` : undefined}
      >
      <FieldsGrid
        fields={activeFields}
        values={values}
        errors={errors}
        onFieldChange={onFieldChange}
        docState={docState}
        modeForm={modeForm}
        hasLines={hasLines}
        gridColumns={gridColumns}
        lookupConfigs={lookupConfigs}
        fetcher={fetcher}
        onRecordSelect={onRecordSelect}
        // Sin gutter propio: el de la tarjeta ya lo pone el contenedor de arriba, y
        // sumar otro metía el primer campo 16px más adentro que la primera pestaña de
        // su propia barra. En el acordeón sí hace falta (ver `paddingX`).
        paddingX=""
      />
      </div>
    </div>
  );
}

// ─── HeaderRenderer ────────────────────────────────────────

/**
 * Renderizador del encabezado del documento maestro.
 *
 * Soporta dos modos de visualización: `accordion` (secciones colapsables)
 * y `tabs` (pestañas horizontales). Cada sección agrupa campos renderizados
 * por `FieldRenderer` en un grid responsivo configurable.
 *
 * @see docs/colors.md - Sistema de tokens de color
 * @see MasterDocument.types - HeaderRendererProps, SectionConfig
 *
 * @example
 * ```tsx
 * <HeaderRenderer
 *   mode="accordion"
 *   columns={3}
 *   sections={[{ key: 'general', label: 'Información general', defaultOpen: true }]}
 *   fieldsBySection={{ general: fields }}
 *   values={headerData}
 *   onFieldChange={(key, value) => updateHeader(key, value)}
 *   docState="DRAFT"
 *   mode_form="edit"
 * />
 * ```
 */
export function HeaderRenderer({
  mode,
  columns,
  sections,
  fieldsBySection,
  values,
  errors,
  onFieldChange,
  docState,
  mode_form,
  hasLines = false,
  className,
  lookupConfigs,
  fetcher,
  onRecordSelect,
}: HeaderRendererProps) {
  // Mapa padre → dependientes para limpiar el dependiente al cambiar un padre.
  const dependentsByParent = useMemo(
    () => buildDependentsByParent(lookupConfigs),
    [lookupConfigs],
  );

  const handleFieldChange = useCallback(
    (key: string, value: unknown) => {
      onFieldChange(key, value);
      // Al cambiar un padre, resetea los searchers dependientes (sólo si tienen
      // valor) para no dejar un valor inválido del padre anterior. Sólo ocurre en
      // cambios del usuario, no en la carga programática (get-by-id).
      const dependents = dependentsByParent[key];
      if (dependents) {
        dependents.forEach((depKey) => {
          if (!isFilterValueEmpty(values[depKey])) onFieldChange(depKey, null);
        });
      }
    },
    [onFieldChange, dependentsByParent, values],
  );

  // Errores cuya key no coincide con ningún campo → banner form-level
  const allFieldKeys = new Set(
    Object.values(fieldsBySection).flat().map((f) => f.key),
  );
  const formLevelErrors = errors
    ? Object.entries(errors).filter(([key]) => !allFieldKeys.has(key))
    : [];

  return (
    // SIN tarjeta propia: el encabezado ya vive dentro de la tarjeta blanca del
    // detalle, y envolverlo en otra pintaba una segunda caja concéntrica
    // alrededor de la barra de pestañas (ADR-027 §E.7, «una caja, no dos»). Es
    // justo lo que hacía que las pestañas de MasterDocument se vieran metidas en
    // un recuadro y las de MasterCrud no.
    <div className={clsx('min-w-0', className)}>
      {formLevelErrors.length > 0 && (
        <div
          role="alert"
          className="mx-4 mt-4 rounded-md border border-red-200 bg-red-50 px-4 py-3 dark:border-red-800 dark:bg-red-950/20"
        >
          {formLevelErrors.map(([key, err]) => (
            <p key={key} className="text-sm text-red-700 dark:text-red-400">
              {err.message}
            </p>
          ))}
        </div>
      )}
      {mode === 'accordion' ? (
        sections.map((section) => (
          <AccordionSection
            key={section.key}
            section={section}
            fields={fieldsBySection[section.key] ?? []}
            values={values}
            errors={errors}
            onFieldChange={handleFieldChange}
            docState={docState}
            modeForm={mode_form}
            hasLines={hasLines}
            gridColumns={columns}
            lookupConfigs={lookupConfigs}
            fetcher={fetcher}
            onRecordSelect={onRecordSelect}
          />
        ))
      ) : (
        <TabsRenderer
          sections={sections}
          fieldsBySection={fieldsBySection}
          values={values}
          errors={errors}
          onFieldChange={handleFieldChange}
          docState={docState}
          modeForm={mode_form}
          hasLines={hasLines}
          gridColumns={columns}
          lookupConfigs={lookupConfigs}
          fetcher={fetcher}
          onRecordSelect={onRecordSelect}
        />
      )}
    </div>
  );
}
